import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2, AlertCircle, Calendar, MapPin, Camera, Pencil, Star } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SignaturePad } from "../components/team/SignaturePad";
import { publicApi } from "../lib/api";
import { TERMS_FORM_SNIPPETS } from "../lib/terms";

export const PreFitmentSignOffPage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [confirmName, setConfirmName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await publicApi.getPrefitment(token);
        setState({ loading: false, data, error: null });
        setConfirmName(data.client_name || "");
        if (data.snapshot?.client_signature) {
          setDone(true);
          setSignature(data.snapshot.client_signature);
        }
      } catch (e) {
        setState({ loading: false, data: null, error: e?.response?.data?.detail || "Link not found or expired" });
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!signature) return toast.error("Please sign before submitting.");
    if (!accepted) return toast.error("Please tick the acceptance box.");
    if (!confirmName.trim()) return toast.error("Please confirm your name.");
    setSubmitting(true);
    try {
      await publicApi.signPrefitment(token, { signature, client_name_confirm: confirmName.trim() });
      toast.success("Signed — thank you!");
      setDone(true);
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#f5f6f6] flex items-center justify-center p-6">
        <div className="text-[#3f4b52] inline-flex items-center gap-2">
          <Loader2 className="animate-spin" size={18} /> Loading…
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-[#f5f6f6] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-[#cfd7db] rounded-md p-8 text-center">
          <AlertCircle className="mx-auto text-[#dc2626]" size={36} />
          <h1 className="mt-3 font-display font-black uppercase text-xl text-[#111619]">Link not available</h1>
          <p className="text-sm text-[#3f4b52] mt-2">{state.error}</p>
          <p className="text-xs text-[#3f4b52] mt-4">Please contact Diesel &amp; Dust if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  const { client_name, vehicle_make_model, registration, fitment_address, job_number, snapshot } = state.data;
  const photos = snapshot?.photos || [];
  const damages = snapshot?.damages || [];

  return (
    <div className="min-h-screen bg-[#f5f6f6] text-[#111619]" data-testid="prefitment-page">
      {/* Header */}
      <header className="bg-[#20282c] text-white">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center justify-between gap-3">
          <Logo variant="group" size="sm" dark />
          <div className="text-right">
            <div className="font-display uppercase tracking-widest text-xs text-[#ffc438]">Pre-fitment sign-off</div>
            <div className="text-xs text-white/70">Job #{job_number}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Job summary */}
        <section className="bg-white border border-[#cfd7db] rounded-md p-5">
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Vehicle &amp; appointment</div>
          <h1 className="font-display font-black uppercase text-2xl mt-1">{client_name}</h1>
          <div className="text-sm text-[#3f4b52] mt-1">
            {vehicle_make_model || "—"} · <b className="text-[#111619]">{registration || "—"}</b>
          </div>
          {fitment_address && (
            <div className="flex items-start gap-2 text-sm text-[#3f4b52] mt-3">
              <MapPin size={14} className="mt-0.5 text-[#f28a16] shrink-0" />
              <span>{fitment_address}</span>
            </div>
          )}
          {snapshot?.shared_at && (
            <div className="flex items-center gap-2 text-xs text-[#3f4b52] mt-2">
              <Calendar size={12} /> Shared {new Date(snapshot.shared_at).toLocaleString("en-ZA")}
            </div>
          )}
        </section>

        {/* Photos */}
        {photos.length > 0 && (
          <section className="bg-white border border-[#cfd7db] rounded-md p-5">
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] mb-3 inline-flex items-center gap-2">
              <Camera size={12} /> Pre-fitment photos ({photos.length})
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {photos.map((p, i) => (
                <a key={i} href={p.data_url || p.url} target="_blank" rel="noopener noreferrer" className="block aspect-square bg-[#edf2f4] rounded-md overflow-hidden border border-[#cfd7db]">
                  <img src={p.data_url || p.url} alt={`photo-${i}`} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Damages noted */}
        {damages.length > 0 && (
          <section className="bg-white border border-[#cfd7db] rounded-md p-5">
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] mb-3">
              Existing damage noted ({damages.length})
            </div>
            <ul className="space-y-1.5 text-sm text-[#3f4b52]">
              {damages.map((d, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#f28a16] mt-1.5 shrink-0" />
                  <span>{d.label || d.view || `Damage ${i + 1}`}{d.note ? ` — ${d.note}` : ""}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Notes */}
        {snapshot?.notes && (
          <section className="bg-[#fff7ed] border border-[#fb923c] rounded-md p-4 text-sm text-[#7c2d12]">
            <b className="uppercase tracking-widest text-xs">Fitter notes:</b> {snapshot.notes}
          </section>
        )}

        {/* T&Cs */}
        <section className="bg-white border border-[#cfd7db] rounded-md p-5">
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] mb-3 inline-flex items-center gap-2">
            <ShieldCheck size={12} /> Acceptance &amp; acknowledgements
          </div>
          <ol className="space-y-3 text-sm text-[#3f4b52] list-decimal pl-5">
            <li>{TERMS_FORM_SNIPPETS.preInspection}</li>
            <li>{TERMS_FORM_SNIPPETS.workApproval}</li>
            <li>{TERMS_FORM_SNIPPETS.windscreenSafeDriveAway}</li>
            <li>{TERMS_FORM_SNIPPETS.belongings}</li>
          </ol>
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-xs text-[#f28a16] hover:underline">
            Read full Terms &amp; Conditions →
          </a>
        </section>

        {/* Sign */}
        {done ? (
          <>
            <section className="bg-[#ecfdf5] border border-[#10b981] rounded-md p-6 text-center">
              <CheckCircle2 className="mx-auto text-[#10b981]" size={36} />
              <h2 className="mt-2 font-display font-black uppercase text-xl text-[#065f46]">Thank you — signed</h2>
              <p className="text-sm text-[#3f4b52] mt-1">Your signature has been received. Diesel &amp; Dust will proceed with the fitment as scheduled.</p>
              {signature && (
                <div className="mt-4 bg-white border border-[#cfd7db] rounded-md p-3 max-w-sm mx-auto">
                  <img src={signature} alt="signature" className="w-full" />
                </div>
              )}
              {snapshot?.signed_at && (
                <div className="text-xs text-[#3f4b52] mt-2">
                  Signed {new Date(snapshot.signed_at).toLocaleString("en-ZA")}
                </div>
              )}
            </section>
            <ReviewCollectCard token={token} defaultName={confirmName} />
          </>
        ) : (
          <section className="bg-white border border-[#cfd7db] rounded-md p-5 space-y-4" data-testid="prefitment-sign-block">
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] inline-flex items-center gap-2">
              <Pencil size={12} /> Sign to confirm
            </div>

            <div>
              <Label className="text-xs uppercase text-[#3f4b52]">Your full name</Label>
              <Input
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                className="mt-1.5 bg-white border-[#cfd7db] h-11"
                data-testid="prefitment-name"
              />
            </div>

            <label className="flex items-start gap-3 text-sm text-[#3f4b52] cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                data-testid="prefitment-accept"
                className="h-5 w-5 mt-0.5 accent-[#f28a16] shrink-0"
              />
              <span>
                I confirm the vehicle, condition photos and damages above are accurate, and I accept the Diesel &amp; Dust
                Group Terms &amp; Conditions referenced above.
              </span>
            </label>

            <SignaturePad
              label="Sign here"
              value={signature}
              onChange={setSignature}
              testid="prefitment-signature"
            />

            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="prefitment-submit"
              className="w-full bg-[#f28a16] text-white rounded-md py-4 font-display uppercase tracking-widest text-sm shadow-lg inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
              {submitting ? "Submitting…" : "Submit signed acknowledgement"}
            </button>
          </section>
        )}

        <footer className="text-center text-xs text-[#9aa6ad] py-4">
          Diesel &amp; Dust Group · Secure pre-fitment sign-off
        </footer>
      </main>
    </div>
  );
};

const ReviewCollectCard = ({ token, defaultName }) => {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [author, setAuthor] = useState(defaultName || "");
  const [permission, setPermission] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!permission) return toast.error("Tick the permission checkbox to publish your review.");
    if (rating < 1) return toast.error("Please choose a rating.");
    setSubmitting(true);
    try {
      await publicApi.submitTestimonial({
        token, rating, comment: comment.trim(),
        author_name: author.trim(),
        permission_to_publish: true,
      });
      toast.success("Thank you — your review will appear once approved.");
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit review");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <section className="bg-white border border-[#cfd7db] rounded-md p-5 text-center" data-testid="review-thanks">
        <Star size={28} className="mx-auto text-[#f28a16] fill-[#f28a16]" />
        <div className="mt-2 font-display font-black uppercase text-lg text-[#111619]">Review submitted</div>
        <p className="text-xs text-[#3f4b52] mt-1">It will appear on the Diesel &amp; Dust site once our team approves it.</p>
      </section>
    );
  }

  return (
    <section className="bg-white border border-[#cfd7db] rounded-md p-5 space-y-4" data-testid="review-collect">
      <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] inline-flex items-center gap-2">
        <Star size={12} /> Leave a review (optional)
      </div>
      <p className="text-sm text-[#3f4b52]">
        Happy with the service? A quick word from you helps other drivers choose us — and it only takes 20 seconds.
      </p>

      <div>
        <Label className="text-xs uppercase text-[#3f4b52]">Your rating</Label>
        <div className="mt-1.5 flex items-center gap-1" data-testid="review-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              aria-label={`${n} star${n === 1 ? "" : "s"}`}
              className="p-1"
              data-testid={`review-star-${n}`}
            >
              <Star
                size={28}
                className={(hover || rating) >= n ? "fill-[#f28a16] text-[#f28a16]" : "text-[#cfd7db]"}
              />
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase text-[#3f4b52]">Your name (as it will appear)</Label>
        <Input value={author} onChange={(e) => setAuthor(e.target.value)} className="mt-1.5 bg-white border-[#cfd7db] h-11" data-testid="review-name" />
      </div>

      <div>
        <Label className="text-xs uppercase text-[#3f4b52]">Your review (optional)</Label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          maxLength={400}
          placeholder="Tell us about your experience"
          data-testid="review-comment"
          className="mt-1.5 w-full px-3 py-2 bg-white border border-[#cfd7db] rounded-md text-sm"
        />
        <div className="text-xs text-[#9aa6ad] text-right mt-0.5">{comment.length}/400</div>
      </div>

      <label className="flex items-start gap-3 text-sm text-[#3f4b52] cursor-pointer">
        <input
          type="checkbox"
          checked={permission}
          onChange={(e) => setPermission(e.target.checked)}
          data-testid="review-permission"
          className="h-5 w-5 mt-0.5 accent-[#f28a16] shrink-0"
        />
        <span>
          I give Diesel &amp; Dust permission to publish this review with my first name on the public site.
          You may withdraw permission at any time by emailing us.
        </span>
      </label>

      <button
        type="button"
        onClick={submit}
        disabled={submitting || !permission}
        data-testid="review-submit"
        className="w-full bg-[#f28a16] text-white rounded-md py-3 font-display uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {submitting ? <Loader2 size={18} className="animate-spin" /> : <Star size={16} />}
        {submitting ? "Submitting…" : "Submit review"}
      </button>
    </section>
  );
};

export default PreFitmentSignOffPage;
